import { LinearProgress } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { DropAlert } from '../../components/drops/DropAlert'
import { DropButton } from '../../components/drops/DropButton'
import { DropLink } from '../../components/drops/DropLink'
import get from '../../components/helpers/Api'
import { sleep } from '../../components/helpers/FormatLinks'
import { getBoostAction, performLogin } from '../../components/helpers/WaxApi'
import Page from '../../components/page/Page'
import { useSharedState } from '../../components/waxplorer/Waxplorer'

const BrokenBullsPage = (props) => {
    const [state, , { login }] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const [isSearching, setIsSearching] = useState(false)
    const [isSwapping, setIsSwapping] = useState(false)

    const [transactionId, setTransactionId] = useState(null)
    const [error, setError] = useState(null)

    const [assets, setAssets] = useState(null)

    const performUserInventorySearch = async () => {
        const query = `search?owner=${userName}&search_type=fucked&author=waxbullsclub&verified=all&limit=100000`

        setIsSearching(true)
        const rawResults = await get(query)
        const tempAssets = []
        for (const result of rawResults) {
            const assetId = result.assetId
            const resultId = result.resultId
            const name = result.name
            const image = result.image

            const asset = { assetId, resultId, name, image }

            tempAssets.push(asset)
        }
        setAssets(tempAssets)
        setIsSearching(false)
    }

    useEffect(() => {
        if (userName) {
            performUserInventorySearch()
        }
    }, [userName])

    const performSwap = async (asset) => {
        setError(null)
        setTransactionId(null)
        setIsSwapping(true)

        const actions = [getBoostAction(activeUser)]

        const transferAssetsData = {
            authorized_account: userName,
            asset_ids: [asset.assetId],
            from: userName,
            to: 'nfthivedrops',
            memo: 'swap',
        }

        const transferAssetsAction = {
            account: 'atomicassets',
            name: 'transfer',
            authorization: [
                {
                    actor: userName,
                    permission: activeUser.requestPermission
                        ? activeUser.requestPermission
                        : 'active',
                },
            ],
            data: transferAssetsData,
        }

        actions.push(transferAssetsAction)

        const swapPfpData = {
            owner: userName,
            asset_id: asset.assetId,
            result_id: asset.resultId,
        }

        const swapPfpAction = {
            account: 'nfthivedrops',
            name: 'swappfp',
            authorization: [
                {
                    actor: userName,
                    permission: activeUser.requestPermission
                        ? activeUser.requestPermission
                        : 'active',
                },
            ],
            data: swapPfpData,
        }

        actions.push(swapPfpAction)

        let transactionId = null

        try {
            const result = await activeUser.session.transact(
                {
                    actions: actions,
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )

            transactionId = result?.response?.transaction_id
        } catch (e) {
            setError(e.message)
        }

        setTransactionId(transactionId)

        await sleep(2000)
        setIsSwapping(false)
        performUserInventorySearch()
    }

    return (
        <Page
            banners={false}
            id={'BrokenBullsPage'}
            title="Replace your broken bulls"
            {...props}
        >
            <div className="text-2xl text-center my-10">
                Replace a broken NFT from the Wax Bulls Swag Club Collection
            </div>

            <DropAlert
                className="flex justify-center"
                severity="error"
                title="Please Log In"
                condition={!userName}
            >
                You need to be logged in so we can display any Bulls from your
                inventory.{' '}
                <span
                    className="text-primary hover:underline cursor-pointer"
                    onClick={() => login()}
                >
                    Click here to log in
                </span>
            </DropAlert>

            <DropAlert
                className="flex justify-center"
                condition={userName && isSearching}
                severity="info"
                title="Searching for broken Wax Bulls Swag Club Assets in your inventory..."
            >
                <LinearProgress />
            </DropAlert>

            <DropAlert
                className="flex justify-center"
                condition={userName && isSwapping}
                severity="info"
                title="Swapping your Bull for a new one..."
            >
                <LinearProgress />
            </DropAlert>

            <DropAlert
                className="flex justify-center"
                condition={
                    userName &&
                    !isSwapping &&
                    !isSearching &&
                    assets?.length > 0
                }
                severity="info"
                title="We found some broken Wax Bulls Swag Club Assets in your inventory!"
            >
                If you received an NFT with a broken image from the first Wax
                Bulls Swag Club Assets Drop, you can request to get a new,
                correct one here. Doing so will <b>burn your existing NFT</b>,
                and we will mint a new one for you (with <b>the same traits</b>{' '}
                as before).
            </DropAlert>

            <DropAlert
                className="flex justify-center"
                condition={userName && !isSearching && assets?.length === 0}
                severity="info"
                title="We couldn't find any broken Wax Bulls Swag Club Assets in your inventory!"
            >
                If you think this is a mistake, please reach out to us on
                telegram or discord.
            </DropAlert>

            <DropAlert
                className="flex justify-center"
                severity="success"
                condition={transactionId}
                transactionId={transactionId}
                title="Swap successful!"
            >
                It can take a while for the new NFT to be minted and to appear
                in your inventory
            </DropAlert>

            <DropAlert
                className="flex justify-center"
                severity="error"
                condition={error}
                title="An error occured!"
            >
                {error}
            </DropAlert>

            <div className="flex flex-wrap justify-center">
                {userName && !isSwapping && !isSearching && assets?.length > 0
                    ? assets.map((asset, index) => (
                          <div
                              id={'bull_' + index}
                              className="m-3 p-3 border border-primary rounded-md"
                          >
                              <img src={asset.image} className="h-48 w-48" />
                              <div className="text-center mt-3 font-bold">
                                  {asset.name}
                              </div>
                              <div className="flex justify-center">
                                  <DropLink
                                      href={`/asset/${asset.assetId}`}
                                      external
                                      className="text-sm text-primary hover:underline"
                                  >
                                      {asset.assetId}
                                  </DropLink>
                              </div>
                              <div className="text-center mt-3 italic text-sm">
                                  {asset.resultId
                                      ? 'resultId: ' + asset.resultId
                                      : 'Missing resultId!'}
                              </div>
                              <DropButton
                                  size="small"
                                  className="flex justify-center mt-3"
                                  disabled={!asset.resultId}
                                  onClick={() => performSwap(asset)}
                              >
                                  Request Replacement
                              </DropButton>
                          </div>
                      ))
                    : null}
            </div>
        </Page>
    )
}

export default BrokenBullsPage
